
import { Fragment, useEffect, useRef, useState } from 'react';
import './CesiumContainer.css'
import * as Cesium from "cesium";
import useGroundStationData from '../../../../hooks/useGroundStationData';
import useCesium from '../../../../hooks/useCesium';
import GroundTargetCard from '../../../../../CommonComponents/GroundTargetCard'
import GroundStationCard from '../../../../../CommonComponents/GroundStationCard';
import { getEntityDetails } from '../../../../../../utils/cesiumUtil';
import MultiGroundObjectCard from '../../../../../CommonComponents/MultiGroundObjectCard';

const CesiumContainer = () => {
    const { cesiumContainerRef, map_viewer, groundTargets } = useCesium()
    const { all_ground_stations } = useGroundStationData()
    const [selected_gs, set_selected_gs] = useState<any>(null)
    const [selected_task, set_selected_task] = useState<any>(null)
    const [selected_multi_object, set_selected_multi_object] = useState<any>(null)
    const hoveredGsEntityRef = useRef<any>(null)
    const hoveredGsImageRef = useRef<string>('')

    const restoreHoveredGs = () => {
        if (hoveredGsEntityRef.current?.billboard) {
            hoveredGsEntityRef.current.billboard.image = hoveredGsImageRef.current
            hoveredGsEntityRef.current = null
            map_viewer?.scene.requestRender()
        }
    }

    useEffect(() => {
        if (!map_viewer || !all_ground_stations || !groundTargets) {
            return; // Exit early if map_viewer or all_ground_stations are not ready
        }
        const handler = new Cesium.ScreenSpaceEventHandler(map_viewer.scene.canvas);
        handler.setInputAction((movement: { endPosition: Cesium.Cartesian2 }) => {
            if (!map_viewer || !map_viewer.scene) return;
            const pickedObjects = map_viewer.scene.drillPick(movement.endPosition);
            try {
                if (pickedObjects && pickedObjects.length > 0) {
                    let stationEntity: any[] = [];
                    let stationPositions: { latitude: number, longitude: number }[] = [];
                    let targetEntity: any[] = [];
                    let targetPositions: { latitude: number, longitude: number }[] = [];
                    let stationCesiumEntity: any = null;

                    pickedObjects.forEach((pickedObject: any) => {
                        if (Cesium.defined(pickedObject) && Cesium.defined(pickedObject.id)) {
                            const entity = pickedObject.id;
                            const description =
                                (entity && entity.description) &&
                                entity.description.getValue(Cesium.JulianDate.now());
                            const details = getEntityDetails(description, entity?._id, all_ground_stations, groundTargets)
                            if (details.stationEntity.length > 0 && !stationCesiumEntity) {
                                stationCesiumEntity = entity
                            }
                            stationEntity = [...stationEntity, ...details.stationEntity]
                            stationPositions = [...stationPositions, ...details.stationPositions]
                            targetEntity = [...targetEntity, ...details.targetEntity]
                            targetPositions = [...targetPositions, ...details.targetPositions]
                        }
                    });

                    // Update hover SVG
                    if (stationCesiumEntity && stationCesiumEntity !== hoveredGsEntityRef.current) {
                        restoreHoveredGs()
                        hoveredGsImageRef.current = stationCesiumEntity.billboard?.image?.getValue?.(Cesium.JulianDate.now()) ?? '/assets/un_select_gs.svg'
                        stationCesiumEntity.billboard.image = '/assets/gs_hover.svg'
                        hoveredGsEntityRef.current = stationCesiumEntity
                        map_viewer.scene.requestRender()
                    } else if (!stationCesiumEntity) {
                        restoreHoveredGs()
                    }

                    // Check how many entities are picked
                    const hasMultipleStations = stationEntity.length > 1;
                    const hasMultipleTargets = targetEntity.length > 1;
                    const hasBothOne = stationEntity.length === 1 && targetEntity.length === 1;
                    if (hasMultipleStations || hasMultipleTargets || hasBothOne) {
                        // Collect all positions
                        const allPositions = [...stationPositions, ...targetPositions];
                        const minLat = Math.min(...allPositions.map(p => p.latitude)) || 0;
                        const maxLon = Math.max(...allPositions.map(p => p.longitude)) || 0;
                        set_selected_multi_object({
                            stations: stationEntity,
                            targets: targetEntity,
                            latitude: minLat,
                            longitude: maxLon,
                        });
                        set_selected_gs(null);
                        set_selected_task(null);
                    } else if (!hasBothOne && stationEntity.length === 1) {
                        set_selected_gs(stationEntity[0]);
                        set_selected_task(null);
                        set_selected_multi_object(null);
                    } else if (!hasBothOne && targetEntity.length === 1) {
                        set_selected_task(targetEntity[0]);
                        set_selected_gs(null);
                        set_selected_multi_object(null);
                    } else {
                        set_selected_task(null);
                        set_selected_gs(null);
                        set_selected_multi_object(null);
                    }
                    document.body.style.cursor = "pointer";
                } else {
                    restoreHoveredGs()
                    set_selected_task(null);
                    set_selected_gs(null);
                    set_selected_multi_object(null);
                    document.body.style.cursor = "auto";
                }
            } catch (error) {
                console.log(error);
            }
        }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        return () => {
            handler.destroy();
        };
    }, [map_viewer, all_ground_stations, groundTargets])



    return (
        <Fragment>
            <GroundStationCard station={selected_gs} viewer={map_viewer} />
            <GroundTargetCard target={selected_task} viewer={map_viewer} />
            {selected_multi_object && <MultiGroundObjectCard assets={selected_multi_object} viewer={map_viewer} />}
            <div ref={cesiumContainerRef} className="position-relative h-100 w-100" />
        </Fragment>
    )
}

export default CesiumContainer